import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  green:"#0a6e4e", greenD:"#084f38", greenL:"#e8f5f0",
  gold:"#f5a623", goldL:"#fef9ed", goldD:"#d4891a",
  navy:"#0f1923", inkM:"#2d3d4f", inkL:"#6b7a8d",
  border:"#e2e8e5", surface:"#f7f9f8", white:"#ffffff",
  blue:"#1d6fcc", blueL:"#dbeafe",
  red:"#dc2626", redL:"#fee2e2",
};

function ProgressRing({ pct, size = 120, hours, required }) {
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const fill = Math.min(pct / 100, 1) * circ;
  const color = pct >= 100 ? "#16a34a" : pct >= 75 ? C.green : pct >= 50 ? C.gold : C.blue;
  return (
    <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={`${color}22`} strokeWidth={10} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={10}
          strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
          style={{ transition:"stroke-dasharray 1.2s cubic-bezier(.22,1,.36,1)" }} />
      </svg>
      <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center" }}>
        <div style={{ fontFamily:"'Fraunces',serif", fontSize:size < 100 ? 18 : 24,
          fontWeight:900, color:C.navy, lineHeight:1 }}>{hours}</div>
        <div style={{ fontSize:size < 100 ? 9 : 11, color:C.inkL, marginTop:2 }}>
          of {required} hrs
        </div>
        <div style={{ fontSize:size < 100 ? 9 : 11, fontWeight:800, color, marginTop:2 }}>
          {pct}%
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color, onClick }) {
  return (
    <div onClick={onClick}
      style={{ background:C.white, border:`1.5px solid ${C.border}`,
        borderRadius:14, padding:"14px 16px", cursor: onClick ? "pointer" : "default",
        transition:"all .15s", flex:1 }}
      onMouseEnter={e => onClick && (e.currentTarget.style.borderColor = color)}
      onMouseLeave={e => onClick && (e.currentTarget.style.borderColor = C.border)}>
      <div style={{ fontSize:22, marginBottom:6 }}>{icon}</div>
      <div style={{ fontFamily:"'Fraunces',serif", fontSize:20, fontWeight:900,
        color:C.navy }}>{value}</div>
      <div style={{ fontSize:11, color:C.inkL, marginTop:2 }}>{label}</div>
    </div>
  );
}

export default function StudentHome({ user, studentProfile, cvProfile, setPage, notify, isPremium, onUpgrade }) {
  const [recentActivity, setRecentActivity] = useState([]);
  const [topJobs,        setTopJobs]        = useState([]);
  const [pendingCount,   setPendingCount]   = useState(0);
  const [loading,        setLoading]        = useState(true);
  const [greeting,       setGreeting]       = useState("Good morning");
  const [daysLeft,       setDaysLeft]       = useState(null);

  const required = cvProfile?.required_hours || studentProfile?.required_hours || 486;
  const approved = cvProfile?.total_approved || 0;
  const pct      = Math.min(100, Math.round((approved / required) * 100));
  const name     = studentProfile?.full_name?.split(" ")[0] || user?.name?.split(" ")[0] || "Student";

  useEffect(() => {
    const h = new Date().getHours();
    setGreeting(h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening");
    if (!user?.id) return;
    load();
  }, [user?.id]); // eslint-disable-line

  // Don't block render on user — show skeleton if briefly null
  if (!user) return null;

  const load = async () => {
    setLoading(true);
    const [actRes, jobRes, pendRes] = await Promise.all([
      // Recent logbook activity
      supabase.from("logbook_entries")
        .select("id, entry_date, hours, coordinator_approved, supervisor_verified, tasks")
        .eq("student_id", user.id)
        .order("entry_date", { ascending: false })
        .limit(5),
      // Top matching jobs
      supabase.from("job_listings")
        .select("id, slug, title, company_name, location, tags, is_featured, salary")
        .eq("is_active", true)
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(4),
      // Pending logbook entries
      supabase.from("logbook_entries")
        .select("id", { count: "exact", head: true })
        .eq("student_id", user.id)
        .eq("supervisor_verified", false)
        .eq("coordinator_approved", false),
    ]);

    setRecentActivity(actRes.data || []);
    setTopJobs(jobRes.data || []);
    setPendingCount(pendRes.count || 0);

    // Calculate days left based on OJT end date
    if (studentProfile?.ojt_end_date) {
      const diff = Math.ceil((new Date(studentProfile.ojt_end_date) - new Date()) / 86400000);
      setDaysLeft(Math.max(0, diff));
    }

    setLoading(false);
  };

  const STATUS_ITEMS = [
    { key:"profile",   icon:"👤", label:"Profile",     done: studentProfile?.has_bio && studentProfile?.has_skills, page:"profile" },
    { key:"applied",   icon:"📨", label:"Applied",     done: studentProfile?.has_applied, page:"applications" },
    { key:"logbook",   icon:"📓", label:"Logbook",     done: studentProfile?.has_logbook, page:"logbook" },
    { key:"docs",      icon:"📄", label:"Documents",   done: studentProfile?.has_document, page:"documents" },
  ];

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif", padding:"0 0 32px" }}>
      <style>{`
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}
        @keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
        .sk{background:linear-gradient(90deg,#e8ede9 25%,#f4f6f5 50%,#e8ede9 75%);background-size:400px 100%;animation:shimmer 1.4s infinite;border-radius:8px}
        .home-card{background:#fff;border:1.5px solid ${C.border};border-radius:16px;padding:18px 20px;margin-bottom:16px;animation:fadeUp .4s ease both}
        .activity-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
        .job-chip{font-size:11px;font-weight:600;padding:3px 9px;border-radius:100px;background:${C.greenL};color:${C.green}}
        .job-chip.featured{background:${C.goldL};color:${C.goldD}}
      `}</style>

      {/* Hero greeting + progress */}
      <div style={{ background:`linear-gradient(135deg,${C.navy} 0%,#1a3a4a 100%)`,
        borderRadius:20, padding:"24px 20px", marginBottom:16,
        position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:-40, right:-40, width:180, height:180,
          borderRadius:"50%", background:"rgba(10,110,78,.12)" }} />
        <div style={{ position:"absolute", bottom:-50, left:-20, width:140, height:140,
          borderRadius:"50%", background:"rgba(245,166,35,.08)" }} />
        <div style={{ position:"relative", display:"flex", gap:16, alignItems:"center" }}>
          <ProgressRing pct={pct} size={110} hours={approved} required={required} />
          <div style={{ flex:1 }}>
            <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,.5)",
              textTransform:"uppercase", letterSpacing:".08em" }}>{greeting}</div>
            <h2 style={{ fontFamily:"'Fraunces',serif", fontSize:22, fontWeight:900,
              color:"#fff", margin:"4px 0 8px", lineHeight:1.2 }}>{name}! 👋</h2>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {daysLeft !== null && (
                <div style={{ background:"rgba(255,255,255,.1)", borderRadius:8,
                  padding:"5px 10px", fontSize:11, color:"rgba(255,255,255,.8)", fontWeight:700 }}>
                  ⏱ {daysLeft} days left
                </div>
              )}
              {pendingCount > 0 && (
                <div onClick={() => setPage("logbook")}
                  style={{ background:"rgba(245,166,35,.25)", borderRadius:8,
                    padding:"5px 10px", fontSize:11, color:C.gold, fontWeight:700, cursor:"pointer" }}>
                  ⚠️ {pendingCount} pending entr{pendingCount > 1 ? "ies" : "y"}
                </div>
              )}
              {pct >= 100 && (
                <div style={{ background:"rgba(22,163,74,.3)", borderRadius:8,
                  padding:"5px 10px", fontSize:11, color:"#4ade80", fontWeight:700 }}>
                  🎉 OJT Complete!
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div style={{ display:"flex", gap:8, marginTop:16, position:"relative" }}>
          <button onClick={() => setPage("logbook")}
            style={{ flex:1, background:C.green, color:"#fff", border:"none",
              borderRadius:10, padding:"11px", fontWeight:800, fontSize:12,
              cursor:"pointer", fontFamily:"inherit" }}>
            📓 Log Today
          </button>
          <button onClick={() => setPage("jobs")}
            style={{ flex:1, background:"rgba(255,255,255,.12)",
              color:"#fff", border:"1px solid rgba(255,255,255,.2)",
              borderRadius:10, padding:"11px", fontWeight:800, fontSize:12,
              cursor:"pointer", fontFamily:"inherit" }}>
            💼 Find Jobs
          </button>
          {pct >= 100 && (
            <button onClick={() => setPage("logbook")}
              style={{ flex:1, background:C.gold, color:C.navy, border:"none",
                borderRadius:10, padding:"11px", fontWeight:800, fontSize:12,
                cursor:"pointer", fontFamily:"inherit" }}>
              🏆 Certificate
            </button>
          )}
        </div>
      </div>

      {/* OJT Status checklist */}
      <div className="home-card" style={{ animationDelay:".05s" }}>
        <div style={{ fontWeight:800, fontSize:14, color:C.navy, marginBottom:14 }}>
          OJT Checklist
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          {STATUS_ITEMS.map(item => (
            <div key={item.key} onClick={() => setPage(item.page)}
              style={{ display:"flex", alignItems:"center", gap:10,
                padding:"10px 12px", borderRadius:10, cursor:"pointer",
                background: item.done ? C.greenL : C.surface,
                border:`1px solid ${item.done ? C.green+"44" : C.border}`,
                transition:"all .15s" }}>
              <span style={{ fontSize:18 }}>{item.icon}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:700,
                  color: item.done ? C.green : C.inkM }}>{item.label}</div>
              </div>
              <span style={{ fontSize:14 }}>{item.done ? "✓" : "→"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent activity */}
      <div className="home-card" style={{ animationDelay:".1s" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div style={{ fontWeight:800, fontSize:14, color:C.navy }}>Recent Logbook</div>
          <button onClick={() => setPage("logbook")}
            style={{ fontSize:12, fontWeight:700, color:C.green, background:"none",
              border:"none", cursor:"pointer", fontFamily:"inherit" }}>
            View all →
          </button>
        </div>
        {loading ? (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {[1,2,3].map(i => <div key={i} className="sk" style={{ height:44 }} />)}
          </div>
        ) : recentActivity.length === 0 ? (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📓</div>
            <div style={{ fontSize:13, color:C.inkL }}>No entries yet</div>
            <button onClick={() => setPage("logbook")}
              style={{ marginTop:10, background:C.green, color:"#fff", border:"none",
                borderRadius:8, padding:"8px 18px", fontWeight:700, fontSize:12,
                cursor:"pointer", fontFamily:"inherit" }}>
              Log your first entry →
            </button>
          </div>
        ) : (
          recentActivity.map(e => (
            <div key={e.id} style={{ display:"flex", alignItems:"center", gap:10,
              padding:"9px 0", borderBottom:`1px solid ${C.border}` }}>
              <div className="activity-dot" style={{
                background: e.coordinator_approved ? C.green :
                            e.supervisor_verified  ? C.gold  : C.inkL }} />
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.navy }}>
                  {new Date(e.entry_date + "T00:00:00").toLocaleDateString("en-PH",
                    { weekday:"short", month:"short", day:"numeric" })}
                  {" · "}{e.hours}h
                </div>
                <div style={{ fontSize:11, color:C.inkL,
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                  maxWidth:220 }}>
                  {e.tasks || "No tasks recorded"}
                </div>
              </div>
              <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px",
                borderRadius:100,
                background: e.coordinator_approved ? C.greenL :
                            e.supervisor_verified  ? C.goldL  : C.surface,
                color:      e.coordinator_approved ? C.green  :
                            e.supervisor_verified  ? C.goldD  : C.inkL }}>
                {e.coordinator_approved ? "✓ Approved" :
                 e.supervisor_verified  ? "Verified"   : "Pending"}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Top job matches */}
      <div className="home-card" style={{ animationDelay:".15s" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div style={{ fontWeight:800, fontSize:14, color:C.navy }}>Top OJT Matches</div>
          <button onClick={() => setPage("jobs")}
            style={{ fontSize:12, fontWeight:700, color:C.green, background:"none",
              border:"none", cursor:"pointer", fontFamily:"inherit" }}>
            See all →
          </button>
        </div>
        {loading ? (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {[1,2].map(i => <div key={i} className="sk" style={{ height:60 }} />)}
          </div>
        ) : topJobs.length === 0 ? (
          <div style={{ textAlign:"center", padding:"16px 0", fontSize:13, color:C.inkL }}>
            No active job listings right now
          </div>
        ) : (
          topJobs.map(job => (
            <div key={job.id} onClick={() => setPage("jobs")}
              style={{ display:"flex", gap:12, alignItems:"center",
                padding:"10px 0", borderBottom:`1px solid ${C.border}`,
                cursor:"pointer" }}>
              <div style={{ width:36, height:36, borderRadius:8, flexShrink:0,
                background: job.is_featured ? C.goldL : C.greenL,
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>
                🏢
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
                  <span style={{ fontSize:13, fontWeight:800, color:C.navy }}>{job.title}</span>
                  {job.is_featured && <span className="job-chip featured">⭐ Featured</span>}
                </div>
                <div style={{ fontSize:11, color:C.inkL }}>
                  {job.company_name} · {job.location}
                  {job.salary && ` · ₱${Number(job.salary).toLocaleString()}/mo`}
                </div>
              </div>
              <span style={{ fontSize:12, color:C.green, fontWeight:700, flexShrink:0 }}>Apply →</span>
            </div>
          ))
        )}
      </div>

      {/* Premium upsell if not premium */}
      {!isPremium && (
        <div onClick={onUpgrade}
          style={{ background:`linear-gradient(135deg,${C.navy},#1a3a4a)`,
            borderRadius:16, padding:"18px 20px", cursor:"pointer",
            display:"flex", alignItems:"center", gap:14,
            animation:"fadeUp .4s ease both", animationDelay:".2s" }}>
          <span style={{ fontSize:32 }}>⚡</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:800, fontSize:14, color:"#fff" }}>Unlock Premium</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,.6)", marginTop:2 }}>
              Unlimited AI Coach, certificate download, priority placement
            </div>
          </div>
          <div style={{ background:C.gold, color:C.navy, borderRadius:8,
            padding:"8px 14px", fontWeight:800, fontSize:12, flexShrink:0 }}>
            ₱199/mo
          </div>
        </div>
      )}
    </div>
  );
}
